'use client';

import { useCallback, useMemo, useState, type FormEvent } from 'react';
import { postJson } from '@/lib/api/client';
import {
  buildContactPayload,
  resolveErrorMessage,
  toTrimmed
} from './contactForm.helpers';

type ContactFormState = {
  form: {
    name: string;
    email: string;
    phone: string;
    message: string;
  };
  isLoading: boolean;
  isSubmitted: boolean;
  errorMessage: string;
  isSubmitDisabled: boolean;
};

type ContactFormHandlers = {
  update: (key: keyof ContactFormState['form'], value: string) => void;
  handleSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
};

export const useContactForm = (): ContactFormState & ContactFormHandlers => {
  const [form, setForm] = useState<ContactFormState['form']>({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const update = useCallback(
    (key: keyof ContactFormState['form'], value: string) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const isSubmitDisabled = useMemo(() => {
    const name = toTrimmed(form.name);
    const email = toTrimmed(form.email);
    const message = toTrimmed(form.message);
    return isLoading || !name || !email || !message;
  }, [form.email, form.message, form.name, isLoading]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setErrorMessage('');
      setIsSubmitted(false);

      const payload = buildContactPayload(form);
      if (!payload.name || !payload.email || !payload.message) {
        setErrorMessage(
          'Please complete your name, email, and message before submitting.'
        );
        return;
      }

      setIsLoading(true);

      try {
        await postJson('forms/contact/support', payload);

        setIsLoading(false);
        setIsSubmitted(true);
        setForm({ name: '', email: '', phone: '', message: '' });
      } catch (error) {
        setIsLoading(false);
        setErrorMessage(resolveErrorMessage(error));
      }
    },
    [form]
  );

  return {
    form,
    isLoading,
    isSubmitted,
    errorMessage,
    isSubmitDisabled,
    update,
    handleSubmit
  };
};
